import React, { useState } from 'react';
import { Project } from '../types';
import { forceSync } from '../services/googleCalendarService';

interface CalendarSyncStatusProps {
  project: Project;
}

const CalendarSyncStatus: React.FC<CalendarSyncStatusProps> = ({ project }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  const handleForceSync = async () => {
    setSyncError(null);
    setSyncing(true);
    try {
      const success = await forceSync(project.id);
      if (success) {
        setLastSynced(new Date());
      } else {
        setSyncError('Synkronisering misslyckades. Försök igen.');
      }
    } catch (error) {
      console.error('Error syncing to calendar:', error);
      setSyncError(error instanceof Error ? error.message : 'Okänt fel');
    } finally {
      setSyncing(false);
    }
  };

  const projectAny = project as any;
  const hasEvalEvent = !!projectAny.calendarEventIds?.evaluation;
  const hasPickupEvent = !!projectAny.calendarEventIds?.pickup;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-medium ${
          hasEvalEvent ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]'
        }`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
          </svg>
          {hasEvalEvent ? 'Hembesök i kalendern' : 'Hembesök inte i kalendern'}
        </span>

        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-medium ${
          hasPickupEvent ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]'
        }`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
          {hasPickupEvent ? 'Hämtning i kalendern' : project.dates.scheduledPickup ? 'Hämtning inte i kalendern' : 'Hämtning saknar datum'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={handleForceSync}
          disabled={syncing}
          className="inline-flex items-center gap-1 px-2.5 min-h-[44px] lg:min-h-[32px] rounded-ios-control text-[12px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50"
        >
          {syncing ? (
            <span className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Synka nu
        </button>

        {lastSynced && (
          <span className="text-xs text-[var(--text-tertiary)]">
            Synkad {lastSynced.toLocaleTimeString('sv-SE')}
          </span>
        )}
      </div>

      {syncError && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{syncError}</p>}

      <p className="text-xs text-[var(--text-tertiary)] opacity-70">
        Kalendern synkas automatiskt när du sparar projektet
      </p>
    </div>
  );
};

export default CalendarSyncStatus;
